/**
 * <s-product-tabs> — makes the product page's in-page tab links behave reliably.
 *
 * The tabs are plain #anchor links (they still work without JavaScript). Native
 * anchor jumps fail in two cases this component fixes:
 *  - The Specifications box is position: sticky on desktop. Once it is stuck,
 *    the browser treats it as "already in view" and does not scroll, so the
 *    click appears to do nothing. We scroll to its non-sticky row instead.
 *  - Clicking the same link twice does nothing, because the hash is unchanged.
 *
 * On every click the target is scrolled below the sticky tab bar, the clicked
 * tab is marked current, the panel gets a brief highlight, and keyboard focus
 * moves to the panel so screen-reader users hear where they landed. While the
 * shopper scrolls, the tab for the panel in view is marked current.
 */
const ACTIVE = ['border-accent', 'font-bold'];
const INACTIVE = ['border-transparent', 'font-semibold', 'text-on-surface-secondary', 'hover:border-border', 'hover:text-on-surface'];

class SProductTabs extends HTMLElement {
  #links = [];
  #ac = null;
  #ticking = false;
  #lockUntil = 0;

  connectedCallback() {
    this.#links = [...this.querySelectorAll('a[href^="#"]')].filter((a) => document.getElementById(a.hash.slice(1)));
    if (!this.#links.length) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || !this.#links.includes(link)) return;
      const panel = document.getElementById(link.hash.slice(1));
      if (!panel) return;
      event.preventDefault();
      this.#go(link, panel);
    }, opts);

    window.addEventListener('scroll', () => {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(() => {
        this.#ticking = false;
        if (performance.now() < this.#lockUntil) return;
        this.#syncToScroll();
      });
    }, { ...opts, passive: true });

    // Deep links such as /products/x#product-specs.
    const initial = this.#links.find((a) => a.hash === window.location.hash);
    if (initial) requestAnimationFrame(() => this.#go(initial, document.getElementById(initial.hash.slice(1)), false));
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #go(link, panel, updateHash = true) {
    const scrollTarget = getComputedStyle(panel).position === 'sticky' ? panel.parentElement : panel;
    const barHeight = this.getBoundingClientRect().height;
    const top = scrollTarget.getBoundingClientRect().top + window.scrollY - barHeight - 16;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#setCurrent(link);
    this.#lockUntil = performance.now() + 900;
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });

    if (updateHash) history.replaceState(history.state, '', link.hash);

    if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
    panel.focus({ preventScroll: true });

    panel.removeAttribute('data-tab-arrived');
    void panel.offsetWidth; // restart the highlight on repeat clicks
    panel.setAttribute('data-tab-arrived', '');
    setTimeout(() => panel.removeAttribute('data-tab-arrived'), 1600);
  }

  #syncToScroll() {
    const line = this.getBoundingClientRect().bottom + 40;
    let current = this.#links[0];
    for (const link of this.#links) {
      const panel = document.getElementById(link.hash.slice(1));
      // A sticky panel sits beside other content, so it is only marked current when clicked.
      if (getComputedStyle(panel).position === 'sticky') continue;
      if (panel.getBoundingClientRect().top <= line) current = link;
    }
    this.#setCurrent(current);
  }

  #setCurrent(current) {
    for (const link of this.#links) {
      const on = link === current;
      link.classList.remove(...(on ? INACTIVE : ACTIVE));
      link.classList.add(...(on ? ACTIVE : INACTIVE));
      if (on) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    }
  }
}

customElements.define('s-product-tabs', SProductTabs);
